<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12">

                <!-- ── Slave: Seleziona congregazione ───────────────────── -->
                <SelectCongregazione
                    v-if="activeSlaveView === 'congregazione'"
                    :items="listaCongregazioni"
                    :titolo="isInterno ? 'Congregazione di provenienza' : 'Congregazione di destinazione'"
                    @selected="onCongSelected"
                    @close="activeSlaveView = null"
                />

                <!-- ── Slave: Seleziona oratore ─────────────────────────── -->
                <SelectOratore
                    v-else-if="activeSlaveView === 'oratore'"
                    :items="listaOratoriFiltrata"
                    :loading="loadingOratori"
                    :filtroOratori="filtroOratori"
                    :nomeCongregazioneArrivo="nomeCongregazioneArrivo"
                    :congregazioneFiltro="isInterno ? (!congregazioneLocked ? congregazioneSelezionata : null) : congregazioneCorrenteAttiva"
                    :schemaFiltroNumero="schemaFirstMode ? schemaSelezionatoNumero : null"
                    @selected="onOratoreSelected"
                    @update:filtroOratori="onFiltroOratoriChangeSlave"
                    @close="activeSlaveView = null"
                />

                <!-- ── Slave: Seleziona schema ──────────────────────────── -->
                <SelectSchema
                    v-else-if="activeSlaveView === 'schema'"
                    :items="schemiVisibili"
                    :loading="loadingSchemi || loadingSchemiAll"
                    :isInterno="isInterno"
                    :soloSchemiNonSvolti="soloSchemiNonSvolti"
                    :oratoreFiltro="oratoreSelezionato || programma.oratore"
                    :congregazioneFiltro="!isInterno ? congregazioneSelezionata : null"
                    @selected="onSchemaSelected"
                    @update:soloSchemiNonSvolti="soloSchemiNonSvolti = $event"
                    @close="activeSlaveView = null"
                />

                <!-- ── Form principale ──────────────────────────────────── -->
                <template v-else>

                <!-- Header -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3">
                    <div class="d-flex align-center">
                        <v-btn v-if="!isInline" icon small text color="primary" @click="chiudi()">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <div class="ml-2 flex-grow-1">
                            <div class="text-subtitle-2 font-weight-bold primary--text">
                                {{ isNew ? 'Nuovo appuntamento' : 'Modifica appuntamento' }}
                                {{ isInterno ? 'Interno' : 'Esterno' }}
                            </div>
                            <div v-if="dataLocale" class="text-caption grey--text text-capitalize">{{ dataLocale }}</div>
                        </div>
                        <v-icon :color="isInterno ? 'primary' : 'teal'" size="28">
                            {{ isInterno ? 'mdi-home-outline' : 'mdi-earth' }}
                        </v-icon>
                    </div>
                </v-card>

                <!-- Skeleton -->
                <v-card v-if="loadingInit" class="rounded-xl elevation-2 pa-4">
                    <v-skeleton-loader type="list-item-two-line, list-item-two-line, list-item-two-line, actions" />
                </v-card>

                <!-- Form -->
                <v-card v-else class="rounded-xl elevation-2">
                    <v-card-text class="pt-4 pb-2">

                        <!-- Banner da confermare (edit mode, provvisorio) -->
                        <v-alert v-if="!isNew && isProvvisorio"
                                 color="orange darken-1" dark dense
                                 class="mb-4 rounded-lg"
                                 icon="mdi-clock-alert-outline">
                            <div class="text-body-2 font-weight-bold">In attesa di conferma</div>
                            <div class="text-caption">Puoi modificare solo schema e cantico.</div>
                        </v-alert>

                        <v-form ref="form">

                            <!-- ══════════════════════════════════════════════════
                                 ESTERNO: Congregazione → Oratore → Schema
                                 ══════════════════════════════════════════════════ -->
                            <template v-if="!isInterno">

                                <!-- Nota guida esterno -->
                                <div v-if="isNew" class="text-caption grey--text mb-3 px-1" style="line-height:1.4">
                                    <v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>
                                    Selezionare prima la congregazione esterna, poi l'oratore interno e infine lo schema.
                                </div>

                                <!-- 1. CONGREGAZIONE DESTINAZIONE -->
                                <div class="field-label">
                                    <v-icon x-small color="grey darken-1" class="mr-1">mdi-home-city-outline</v-icon>
                                    Congregazione di destinazione
                                </div>

                                <!-- Nuovo: apre slave -->
                                <template v-if="isNew">
                                    <v-card v-if="congregazioneSelezionata" outlined
                                            class="rounded-lg px-3 py-2 mb-3 selezionato-card"
                                            @click="activeSlaveView = 'congregazione'">
                                        <div class="d-flex align-center">
                                            <v-icon small color="teal" class="mr-2">mdi-home-city-outline</v-icon>
                                            <div class="flex-grow-1">
                                                <div class="text-body-2 font-weight-medium">{{ congregazioneSelezionata.nome }}</div>
                                                <div v-if="congregazioneSelezionata.giorno || congregazioneSelezionata.ora"
                                                     class="text-caption grey--text">
                                                    {{ congregazioneSelezionata.giorno }} {{ congregazioneSelezionata.ora }}
                                                </div>
                                                <div v-if="congregazioneSelezionataSenzaAdmin"
                                                     class="text-caption deep-orange--text d-flex align-center mt-1">
                                                    <v-icon x-small color="deep-orange" class="mr-1">mdi-account-off-outline</v-icon>
                                                    Nessun admin impostato — verrà salvato senza notifica
                                                </div>
                                            </div>
                                            <v-icon small color="grey">mdi-pencil-outline</v-icon>
                                        </div>
                                    </v-card>
                                    <v-btn v-else block outlined rounded color="primary" class="mb-3"
                                           @click="activeSlaveView = 'congregazione'">
                                        <v-icon left>mdi-home-city-outline</v-icon>
                                        Congregazione
                                    </v-btn>
                                </template>

                                <!-- Modifica: readonly -->
                                <v-card v-if="!isNew" outlined class="rounded-lg px-3 py-2 mb-4">
                                    <div class="d-flex align-center">
                                        <v-icon small color="teal" class="mr-2">mdi-home-city-outline</v-icon>
                                        <span class="text-body-2 font-weight-medium">{{ programma.congregazione && programma.congregazione.nome }}</span>
                                        <v-spacer />
                                        <span v-if="programma.congregazione" class="text-caption grey--text">
                                            {{ programma.congregazione.giorno }} {{ programma.congregazione.ora }}
                                        </span>
                                    </div>
                                </v-card>

                                <!-- 2. ORATORE -->
                                <div class="field-label mt-1">
                                    <v-icon x-small color="grey darken-1" class="mr-1">mdi-account-outline</v-icon>
                                    Oratore
                                </div>
                                <!-- Nuovo: apre slave oratore -->
                                <template v-if="isNew">
                                    <!-- Chip congregazione di connessione -->
                                    <div v-if="!oratoreSelezionato && congregazioneCorrenteAttiva"
                                         class="d-flex flex-wrap mb-2" style="gap:6px">
                                        <v-chip small color="teal" dark>
                                            <v-icon x-small left>mdi-home-city-outline</v-icon>
                                            {{ congregazioneCorrenteAttiva.nome }}
                                        </v-chip>
                                        <span class="text-caption grey--text align-self-center">
                                            {{ listaOratoriFiltrata.length ? listaOratoriFiltrata.length + ' oratori' : '' }}
                                        </span>
                                    </div>
                                    <v-card v-if="oratoreSelezionato" outlined
                                            class="rounded-lg px-3 py-2 mb-3 selezionato-card"
                                            @click="activeSlaveView = 'oratore'">
                                        <div class="d-flex align-center">
                                            <v-avatar size="28" color="primary" class="mr-2">
                                                <span class="white--text" style="font-size:0.6rem;font-weight:700">{{ initiali(oratoreSelezionato.nominativo) }}</span>
                                            </v-avatar>
                                            <div class="flex-grow-1">
                                                <div class="d-flex align-center flex-wrap" style="gap:4px">
                                                    <span class="text-body-2 font-weight-medium">{{ oratoreSelezionato.nominativo }}</span>
                                                    <v-chip v-if="oratoreSelezionato.isAnziano"      x-small color="teal" dark>A</v-chip>
                                                    <v-chip v-if="oratoreSelezionato.isSM"           x-small color="blue darken-1" dark>SM</v-chip>
                                                    <v-chip v-if="oratoreSelezionato.isAssistente"   x-small color="grey darken-2" dark>ASS</v-chip>
                                                    <v-chip v-if="oratoreSelezionato.isCoordinatore" x-small color="deep-orange darken-4" dark>ADM</v-chip>
                                                    <chip-login-oratore :oratore="oratoreSelezionato" />
                                                </div>
                                                <div class="text-caption grey--text">{{ oratoreSelezionato.congregazione && oratoreSelezionato.congregazione.nome }}</div>
                                            </div>
                                            <v-icon small color="grey">mdi-pencil-outline</v-icon>
                                        </div>
                                    </v-card>
                                    <v-btn v-else block outlined rounded color="primary" class="mb-3"
                                           :disabled="!congregazioneSelezionata || loadingOratori"
                                           :loading="loadingOratori"
                                           @click="activeSlaveView = 'oratore'">
                                        <v-icon left>mdi-account-outline</v-icon>
                                        Oratore
                                    </v-btn>
                                </template>

                                <!-- Modifica: oratore readonly -->
                                <v-card v-if="!isNew" outlined class="rounded-lg px-3 py-2 mb-4">
                                    <div class="d-flex align-center">
                                        <v-icon small color="primary" class="mr-2">mdi-account-circle-outline</v-icon>
                                        <span class="text-body-2 font-weight-medium">{{ programma.oratore && programma.oratore.nominativo }}</span>
                                        <v-spacer />
                                        <v-chip v-if="programma.oratore && programma.oratore.isAnziano"     x-small color="teal" dark class="mr-1">A</v-chip>
                                        <v-chip v-if="programma.oratore && programma.oratore.isSM"          x-small color="blue darken-1" dark class="mr-1">SM</v-chip>
                                        <v-chip v-if="programma.oratore && programma.oratore.isAssistente"  x-small color="grey darken-2" dark class="mr-1">ASSISTENTE</v-chip>
                                        <v-chip v-if="programma.oratore && programma.oratore.isCoordinatore" x-small color="deep-orange darken-4" dark class="mr-1">ADM</v-chip>
                                        <v-chip v-if="programma.oratore && programma.oratore.isAdmin"       x-small color="orange darken-1" dark>APP-ADM</v-chip>
                                        <chip-login-oratore v-if="programma.oratore" :oratore="programma.oratore" class="ml-1" />
                                    </div>
                                </v-card>

                            </template>

                            <!-- ══════════════════════════════════════════════════
                                 INTERNO: Congregazione ↔ Oratore ↔ Schema
                                 ══════════════════════════════════════════════════ -->
                            <template v-if="isInterno">

                                <!-- Azzera filtri -->
                                <div v-if="isNew && (oratoreSelezionato || congregazioneSelezionata || discorsoId)"
                                     class="d-flex justify-end mb-2">
                                    <v-btn x-small text color="grey darken-1" @click="azzeraFiltri">
                                        <v-icon x-small left>mdi-refresh</v-icon>
                                        Azzera filtri
                                    </v-btn>
                                </div>

                                <!-- Nota guida -->
                                <div v-if="isNew" class="text-caption grey--text mb-3 px-1" style="line-height:1.4">
                                    <v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>
                                    Selezionare inizialmente uno dei campi congregazione, oratore o schema, gli altri saranno filtrati di conseguenza
                                </div>

                                <!-- 0. CONGREGAZIONE PROVENIENZA -->
                                <div class="field-label">
                                    <v-icon x-small color="grey darken-1" class="mr-1">mdi-home-city-outline</v-icon>
                                    Congregazione di provenienza
                                </div>
                                <template v-if="isNew">
                                    <!-- Locked: auto-set dalla scelta oratore -->
                                    <v-card v-if="congregazioneLocked && congregazioneSelezionata"
                                            outlined class="rounded-lg px-3 py-2 mb-3 d-flex align-center">
                                        <v-icon small color="teal" class="mr-2">mdi-home-city-outline</v-icon>
                                        <span class="text-body-2 font-weight-medium flex-grow-1">{{ congregazioneSelezionata.nome }}</span>
                                        <v-icon x-small color="grey darken-1">mdi-lock-outline</v-icon>
                                    </v-card>
                                    <!-- Libera: apre slave congregazione -->
                                    <template v-else>
                                        <v-card v-if="congregazioneSelezionata" outlined
                                                class="rounded-lg px-3 py-2 mb-3 selezionato-card"
                                                @click="activeSlaveView = 'congregazione'">
                                            <div class="d-flex align-center">
                                                <v-icon small color="teal" class="mr-2">mdi-home-city-outline</v-icon>
                                                <div class="flex-grow-1">
                                                    <div class="text-body-2 font-weight-medium">{{ congregazioneSelezionata.nome }}</div>
                                                    <div v-if="congregazioneSelezionata.giorno || congregazioneSelezionata.ora"
                                                         class="text-caption grey--text">
                                                        {{ congregazioneSelezionata.giorno }} {{ congregazioneSelezionata.ora }}
                                                    </div>
                                                    <div v-if="congregazioneSelezionata.prenotazione"
                                                         class="text-caption orange--text darken-1 d-flex align-center mt-1">
                                                        <v-icon x-small color="orange darken-1" class="mr-1">mdi-alert-outline</v-icon>
                                                        {{ congregazioneSelezionata.prenotazione }}
                                                    </div>
                                                    <div v-if="congregazioneSelezionataSenzaAdmin"
                                                         class="text-caption deep-orange--text d-flex align-center mt-1">
                                                        <v-icon x-small color="deep-orange" class="mr-1">mdi-account-off-outline</v-icon>
                                                        Nessun admin impostato — verrà salvato senza notifica
                                                    </div>
                                                </div>
                                                <v-icon small color="grey">mdi-pencil-outline</v-icon>
                                            </div>
                                        </v-card>
                                        <v-btn v-else block outlined rounded color="primary" class="mb-3"
                                               @click="activeSlaveView = 'congregazione'">
                                            <v-icon left>mdi-home-city-outline</v-icon>
                                            Congregazione
                                        </v-btn>
                                    </template>
                                </template>
                                <!-- Modifica: congregazione provenienza readonly -->
                                <v-card v-if="!isNew && programma.congregazioneEsterna"
                                        outlined class="rounded-lg px-3 py-2 mb-3">
                                    <div class="d-flex align-center">
                                        <v-icon small color="teal" class="mr-2">mdi-home-city-outline</v-icon>
                                        <span class="text-body-2 font-weight-medium">{{ programma.congregazioneEsterna.nome }}</span>
                                    </div>
                                </v-card>

                                <!-- 1. ORATORE -->
                                <div class="field-label mt-1">
                                    <v-icon x-small color="grey darken-1" class="mr-1">mdi-account-outline</v-icon>
                                    Oratore
                                </div>

                                <!-- Chips filtri attivi -->
                                <div v-if="isNew && !oratoreSelezionato && (congregazioneSelezionata || schemaFirstMode)"
                                     class="d-flex flex-wrap mb-2" style="gap:6px">
                                    <v-chip v-if="congregazioneSelezionata && !congregazioneLocked"
                                            small color="teal" dark>
                                        <v-icon x-small left>mdi-home-city-outline</v-icon>
                                        {{ congregazioneSelezionata.nome }}
                                    </v-chip>
                                    <v-chip v-if="schemaFirstMode && schemaSelezionatoNumero"
                                            small color="primary" dark>
                                        <v-icon x-small left>mdi-book-open-outline</v-icon>
                                        Schema N.{{ schemaSelezionatoNumero }}
                                    </v-chip>
                                    <span class="text-caption grey--text align-self-center">
                                        {{ listaOratoriFiltrata.length === 0 ? 'Nessun oratore disponibile' : listaOratoriFiltrata.length + ' oratori' }}
                                    </span>
                                </div>

                                <!-- Nuovo: apre slave oratore (interno) -->
                                <template v-if="isNew">
                                    <v-card v-if="oratoreSelezionato" outlined
                                            class="rounded-lg px-3 py-2 mb-3 selezionato-card"
                                            @click="activeSlaveView = 'oratore'">
                                        <div class="d-flex align-center">
                                            <v-avatar size="28" color="primary" class="mr-2">
                                                <span class="white--text" style="font-size:0.6rem;font-weight:700">{{ initiali(oratoreSelezionato.nominativo) }}</span>
                                            </v-avatar>
                                            <div class="flex-grow-1">
                                                <div class="d-flex align-center flex-wrap" style="gap:4px">
                                                    <span class="text-body-2 font-weight-medium">{{ oratoreSelezionato.nominativo }}</span>
                                                    <v-chip v-if="oratoreSelezionato.isAnziano"      x-small color="teal" dark>A</v-chip>
                                                    <v-chip v-if="oratoreSelezionato.isSM"           x-small color="blue darken-1" dark>SM</v-chip>
                                                    <v-chip v-if="oratoreSelezionato.isAssistente"   x-small color="grey darken-2" dark>ASS</v-chip>
                                                    <v-chip v-if="oratoreSelezionato.isCoordinatore" x-small color="deep-orange darken-4" dark>ADM</v-chip>
                                                    <chip-login-oratore :oratore="oratoreSelezionato" />
                                                </div>
                                                <div class="text-caption grey--text">{{ oratoreSelezionato.congregazione && oratoreSelezionato.congregazione.nome }}</div>
                                            </div>
                                            <v-icon small color="grey">mdi-pencil-outline</v-icon>
                                        </div>
                                    </v-card>
                                    <v-btn v-else block outlined rounded color="primary" class="mb-3"
                                           :loading="loadingOratori"
                                           @click="activeSlaveView = 'oratore'">
                                        <v-icon left>mdi-account-outline</v-icon>
                                        Oratore
                                    </v-btn>
                                </template>

                                <!-- Modifica: oratore readonly -->
                                <v-card v-if="!isNew" outlined class="rounded-lg px-3 py-2 mb-4">
                                    <div class="d-flex align-center">
                                        <v-icon small color="primary" class="mr-2">mdi-account-circle-outline</v-icon>
                                        <div class="flex-grow-1">
                                            <span class="text-body-2 font-weight-medium">{{ programma.oratore && programma.oratore.nominativo }}</span>
                                            <div v-if="programma.oratore && programma.oratore.congregazione"
                                                 class="text-caption grey--text">
                                                {{ programma.oratore.congregazione.nome }}
                                            </div>
                                        </div>
                                        <v-chip v-if="programma.oratore && programma.oratore.isAnziano"     x-small color="teal" dark class="mr-1">A</v-chip>
                                        <v-chip v-if="programma.oratore && programma.oratore.isSM"          x-small color="blue darken-1" dark class="mr-1">SM</v-chip>
                                        <v-chip v-if="programma.oratore && programma.oratore.isAssistente"  x-small color="grey darken-2" dark class="mr-1">ASSISTENTE</v-chip>
                                        <v-chip v-if="programma.oratore && programma.oratore.isCoordinatore" x-small color="deep-orange darken-4" dark class="mr-1">ADM</v-chip>
                                        <v-chip v-if="programma.oratore && programma.oratore.isAdmin"       x-small color="orange darken-1" dark>APP-ADM</v-chip>
                                        <chip-login-oratore v-if="programma.oratore" :oratore="programma.oratore" class="ml-1" />
                                    </div>
                                </v-card>
                            </template>

                            <!-- ── SCHEMA (comune) ───────────────────────────────────── -->
                            <div class="field-label mt-1">
                                <v-icon x-small color="grey darken-1" class="mr-1">mdi-book-open-outline</v-icon>
                                Schema
                            </div>
                            <!-- Schema selezionato: card con edit -->
                            <template v-if="isNew">
                                <v-card v-if="discorsoId" outlined
                                        class="rounded-lg px-3 py-2 mb-3 selezionato-card"
                                        :class="schemaDisabled ? 'selezionato-locked' : ''"
                                        @click="!schemaDisabled && (activeSlaveView = 'schema')">
                                    <div class="d-flex align-center">
                                        <span class="numero-schema mr-3 flex-shrink-0">{{ schemaSelezionatoNumero || '—' }}</span>
                                        <span class="text-body-2 flex-grow-1" style="white-space:normal">{{ schemaSelezionatoTema }}</span>
                                        <v-icon v-if="!schemaDisabled" small color="grey">mdi-pencil-outline</v-icon>
                                        <v-icon v-else x-small color="grey darken-1">mdi-lock-outline</v-icon>
                                    </div>
                                </v-card>
                                <v-btn v-else block outlined rounded color="primary" class="mb-3"
                                       :disabled="schemaDisabled"
                                       :loading="loadingSchemi || loadingSchemiAll"
                                       @click="activeSlaveView = 'schema'">
                                    <v-icon left>mdi-book-open-outline</v-icon>
                                    Schema
                                </v-btn>
                            </template>
                            <!-- Modifica INTERNO: card cliccabile → apre SelectSchema slave -->
                            <v-card v-if="!isNew && isInterno" outlined
                                    class="rounded-lg px-3 py-2 mb-3 selezionato-card"
                                    @click="activeSlaveView = 'schema'">
                                <div class="d-flex align-center">
                                    <span class="numero-schema mr-3 flex-shrink-0">{{ schemaSelezionatoNumero || '—' }}</span>
                                    <span class="text-body-2 flex-grow-1" style="white-space:normal">{{ schemaSelezionatoTema }}</span>
                                    <v-icon small color="grey">mdi-pencil-outline</v-icon>
                                </div>
                            </v-card>

                            <!-- Modifica ESTERNO: autocomplete classico -->
                            <v-autocomplete v-if="!isNew && !isInterno"
                                v-model="discorsoId"
                                :items="schemiVisibili"
                                item-value="discorso.id"
                                :item-text="s => s.discorso ? (s.discorso.diId + ' ' + s.discorso.tema) : ''"
                                :filter="filtroSchema"
                                :placeholder="schemiPlaceholder"
                                outlined rounded dense
                                class="mb-1"
                                @change="onSchemaChange"
                            >
                                <template v-slot:selection="{ item }">
                                    <div class="d-flex align-start py-1" style="white-space:normal;word-break:break-word;min-width:0;width:100%">
                                        <span class="numero-schema mr-2 flex-shrink-0">{{ item.discorso && (item.discorso.diId === 99999 ? '-' : (item.discorso.numero ? item.discorso.numero.trim() : item.discorso.diId)) }}</span>
                                        <span style="white-space:normal">{{ item.discorso && item.discorso.tema }}</span>
                                    </div>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-content>
                                        <v-list-item-title class="d-flex align-center">
                                            <span class="numero-schema mr-2">{{ item.discorso && (item.discorso.diId === 99999 ? '-' : (item.discorso.numero ? item.discorso.numero.trim() : item.discorso.diId)) }}</span>
                                            <span>{{ item.discorso && item.discorso.tema }}</span>
                                        </v-list-item-title>
                                        <v-list-item-subtitle v-if="item.ultimaData" class="caption mt-1">
                                            <v-icon x-small class="mr-1" style="opacity:0.5">mdi-calendar-outline</v-icon>
                                            {{ localData(item.ultimaData) }}
                                            <span v-if="item.ultimoOratore" class="ml-1">{{ item.ultimoOratore }}</span>
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>

                            <!-- ── CANTICO ──────────────────────────────────────────── -->
                            <div class="field-label mt-1">
                                <v-icon x-small color="grey darken-1" class="mr-1">mdi-music-note</v-icon>
                                Cantico
                            </div>
                            <v-text-field
                                v-model="cantico"
                                outlined rounded dense
                                placeholder="Numero cantico (opzionale)"
                                type="number"
                                min="0"
                                hide-spin-buttons
                            />                            
                        </v-form>
                    </v-card-text>

                    <v-divider />

                    <v-card-actions class="px-4 py-3">
                        <v-btn text rounded @click="chiudi()">Annulla</v-btn>
                        <v-spacer />
                        <v-btn v-if="mostraConferma"
                               color="success" rounded depressed
                               :loading="loadingSave"
                               :disabled="!formValida"
                               class="mr-2"
                               @click="salva(true)">
                            <v-icon left small>mdi-check-circle-outline</v-icon>
                            Salva e Conferma
                        </v-btn>
                        <v-btn :color="isPrenota ? 'orange darken-1' : 'primary'" rounded depressed
                               :loading="loadingSave"
                               :disabled="!formValida"
                               @click="salva()">
                            <v-icon v-if="isPrenota" left small>mdi-calendar-clock</v-icon>
                            {{ isPrenota ? 'Prenota' : 'Salva' }}
                        </v-btn>
                    </v-card-actions>
                </v-card>

                </template><!-- fine v-else form principale -->

            </v-col>
        </v-row>

        <v-snackbar v-model="snackError" timeout="2500" color="error">
            <v-icon dark class="mr-1">mdi-alert-circle</v-icon>{{ errMsg }}
        </v-snackbar>
    </v-container>
</template>

<script>
import { callService, callPost, gLocalData } from '@/modules/utilities.js';
import { VSkeletonLoader } from 'vuetify/lib';
import { bus } from '@/main.js';
import SelectCongregazione from '@/views/SelectCongregazione.vue';
import SelectOratore from '@/views/SelectOratore.vue';
import SelectSchema from '@/views/SelectSchema.vue';
import ChipLoginOratore from '@/components/ChipLoginOratore.vue';

export default {
    components: { VSkeletonLoader, SelectCongregazione, SelectOratore, SelectSchema, ChipLoginOratore },

    props: {
        inlineId:              { type: [String, Number], default: null },
        inlineTipo:            { type: String, default: null },
        inlineData:            { type: String, default: null },
        inlineIdCongregazione: { type: String, default: null },
    },

    data: () => ({
        activeSlaveView: null,   // 'congregazione' | 'oratore' | 'schema' | null
        loadingInit: false,
        loadingOratori: false,
        loadingSchemi: false,
        loadingSave: false,
        valid: false,
        formSubmitted: false,
        isInterno: false,
        isNew: true,

        // Dati programma (modifica)
        programma: { oratore: null, congregazione: null, discorso: {}, cantico: 0 },
        dataLocale: '',
        dataISO: null,

        // Campi form
        oratoreSelezionato: null,       // Oratore puro estratto da OratoreData
        oratoreDataSelezionato: null,   // OratoreData completo (per v-autocomplete)
        congregazioneSelezionata: null,
        discorsoId: null,
        cantico: null,
        isProvvisorio: true,
        filtroOratori: 'disponibili', // '' | 'disponibili' | 'riserva'
        soloSchemiNonSvolti: true,   // switch "solo discorsi non svolti nell'ultimo anno"

        // Interno: selezione tre-vie
        congregazioneLocked: false,     // true quando auto-set da scelta oratore
        schemaFirstMode: false,         // true quando schema scelto prima dell'oratore
        oratoriPerSchemaIds: [],        // id oratori che hanno lo schema scelto

        // Liste
        listaOratori: [],           // array di OratoreData
        listaSchemiAll: [],         // tutti gli schemi (per schema-first)
        listaCongregazioni: [],
        congregazioneCorrenteAttiva: null,
        schemi: [],                 // schemi dell'oratore selezionato
        loadingSchemiAll: false,

        snackError: false,
        errMsg: '',
    }),

    computed: {
        circoscrizionePrenotazione() {
            return this.$store.state.circoscrizione?.prenotazione === true;
        },
        // "Prenota" (= salva come provvisorio) quando:
        //  - interno nuovo
        //  - la circoscrizione usa le prenotazioni
        //  - la congregazione dell'oratore usa le prenotazioni
        //  - la congregazione dell'oratore NON è la nostra
        //  - la data è futura
        isPrenota() {
            if (!this.isInterno || !this.isNew) return false;
            if (!this.circoscrizionePrenotazione) return false;
            if (this.$store.state.congregazioneCorrente?.prenotazione !== '') return false;
            if (!this.congregazioneSelezionata) return false;
            if (this.congregazioneSelezionata.id === this.idCongregazioneAttiva) return false;
            if (this.congregazioneSelezionata.prenotazione !== '') return false;
            if (!this.congregazioneSelezionata.coordinatore) return false; // nessun admin → salva normale
            if (this.dataISO && new Date(this.dataISO) < new Date()) return false;
            return true;
        },
        congregazioneSelezionataSenzaAdmin() {
            if (!this.congregazioneSelezionata) return false;
            if (!this.circoscrizionePrenotazione) return false;
            if (this.congregazioneSelezionata.prenotazione !== '') return false; // già esclusa, ha già il suo avviso
            return !this.congregazioneSelezionata.coordinatore;
        },
        // Schemi visibili nel select, con filtro "ultimo anno" per interno
        schemiVisibili() {
            let lista;
            if (!this.isNew) {
                lista = this.schemi;
            } else if (this.isInterno && !this.oratoreSelezionato) {
                lista = this.listaSchemiAll;
            } else {
                lista = this.schemi;
            }
            // Filtro schemi non disponibili (oscurati): esclusi in fase di pianificazione
            // "Da stabilire" (diId=99999) è sempre visibile
            lista = lista.filter(s =>
                s.discorso?.diId === 99999 || !s.discorso?.isOscurato
            );
            // Filtro "non svolti nell'ultimo anno" (solo interno)
            if (this.isInterno && this.soloSchemiNonSvolti && lista.length) {
                const unAnnoFa = new Date();
                unAnnoFa.setFullYear(unAnnoFa.getFullYear() - 1);
                lista = lista.filter(s =>
                    s.discorso?.diId === 99999 ||
                    !s.ultimaData ||
                    new Date(s.ultimaData) < unAnnoFa
                );
            }
            // Garantisce che lo schema già selezionato sia sempre presente (evita reset del v-select)
            if (this.discorsoId && !lista.find(s => s.discorso?.id === this.discorsoId)) {
                const trovato = [...this.schemi, ...this.listaSchemiAll]
                    .find(s => s.discorso?.id === this.discorsoId);
                if (trovato) lista = [trovato, ...lista];
            }
            return lista;
        },
        // Schema disabled: interno new sempre abilitato (schema-first), esterno solo dopo oratore
        schemaDisabled() {
            if (this.loadingSchemi || this.loadingSchemiAll) return true;
            if (!this.isNew) return false;
            if (this.isInterno) {
                // Schema-first + oratore già scelto: schema bloccato (si cambia solo con Azzera filtri)
                if (this.schemaFirstMode && this.oratoreSelezionato) return true;
                return false;
            }
            return !this.oratoreSelezionato;
        },
        // Messaggio lista oratori vuota contestuale
        noDataOratore() {
            if (this.congregazioneSelezionata && this.schemaFirstMode && this.schemaSelezionatoNumero)
                return `Nessun oratore di ${this.congregazioneSelezionata.nome} ha lo schema N.${this.schemaSelezionatoNumero}`;
            if (this.congregazioneSelezionata && !this.congregazioneLocked)
                return `Nessun oratore trovato per ${this.congregazioneSelezionata.nome}`;
            if (this.schemaFirstMode && this.schemaSelezionatoNumero)
                return `Nessun oratore con lo schema N.${this.schemaSelezionatoNumero}`;
            return 'Nessun oratore trovato';
        },
        // Numero dello schema scelto in schema-first (per etichetta oratori)
        schemaSelezionatoNumero() {
            if (!this.discorsoId) return null;
            const s = [...this.listaSchemiAll, ...this.schemi]
                .find(s => s.discorso?.id === this.discorsoId);
            return s?.discorso?.numero?.trim() || s?.discorso?.diId || null;
        },
        schemaSelezionatoTema() {
            if (!this.discorsoId) return '';
            const s = [...this.listaSchemiAll, ...this.schemi]
                .find(s => s.discorso?.id === this.discorsoId);
            return s?.discorso?.tema || '';
        },
        // Lista oratori filtrata per congregazione e/o schema (per interno)
        listaOratoriFiltrata() {
            let lista = this.listaOratori;
            // Filtro per congregazione solo in INTERNO (in ESTERNO il server filtra già per appartenenza)
            if (this.isInterno && this.congregazioneSelezionata && !this.congregazioneLocked) {
                lista = lista.filter(od => od.oratore?.congregazione?.id === this.congregazioneSelezionata.id);
            }
            // Filtro per schema (schema-first mode)
            if (this.schemaFirstMode) {
                lista = lista.filter(od => this.oratoriPerSchemaIds.includes(od.oratore?.id));
            }
            return lista;
        },
        schemiPlaceholder() {
            if (this.loadingSchemi || this.loadingSchemiAll) return 'Caricamento schemi...';
            if (this.isInterno && this.isNew && !this.oratoreSelezionato)
                return 'Scegli schema oppure seleziona prima l\'oratore';
            if (!this.oratoreSelezionato && !this.programma.oratore) return 'Prima seleziona un oratore';
            if (this.schemiVisibili.length === 0) return 'Nessuno schema disponibile';
            return 'Seleziona schema';
        },
        nomeCongregazioneArrivo() {
            if (!this.isInterno && this.congregazioneSelezionata) return this.congregazioneSelezionata.nome;
            if (this.isInterno && this.congregazioneCorrenteAttiva) return this.congregazioneCorrenteAttiva.nome;
            return 'questa congregazione';
        },
        // Bottone Salva/Prenota abilitato solo se tutti i campi obbligatori sono valorizzati
        formValida() {
            if (this.loadingSchemi || this.loadingOratori || this.loadingSchemiAll) return false;
            if (this.isNew) {
                if (!this.oratoreSelezionato) return false;
                if (!this.discorsoId) return false;
                if (!this.isInterno && !this.congregazioneSelezionata) return false;
            } else {
                if (!this.discorsoId) return false;
            }
            if (!this.dataISO) return false;
            return true;
        },
        isInline() { return this.inlineId !== null || this.inlineTipo !== null || this.inlineData !== null; },
        idCongregazioneAttiva() {
            return this.inlineIdCongregazione
                || this.$route?.query?.idCong
                || this.$store.state.idCongregazione;
        },
        puoConfermare() {
            const o = this.$store.state.oratore;
            if (!o) return false;
            return o.codice == this.$store.state.superAdminCode || o.isAdmin || o.isCoordinatore || o.isAssistente;
        },
        mostraConferma() {
            return !this.isNew && !this.isInterno && this.isProvvisorio && this.puoConfermare;
        },
    },

    watch: {
        '$route': {
            immediate: true,
            async handler(route) {
                if (this.isInline) return; // gestito da mounted/watch props
                const tipo = route.params.tipo || route.query.tipo;
                this.isInterno = tipo === 'interno';
                this.isNew = !route.params.id;
                this.resetForm();

                if (this.isNew) {
                    this.dataISO    = route.query.data || new Date().toISOString();
                    this.dataLocale = gLocalData(new Date(this.dataISO));
                    await this.loadDatiNew();
                } else {
                    await this.loadProgramma(route.params.id);
                }
            }
        },
        inlineId(val) { if (val) this.loadInline(); },
        inlineData(val) { if (val) this.loadInline(); },
    },

    async mounted() {
        this.congregazioneCorrenteAttiva = this.$store.state.congregazioneCorrente;
        if (this.isInline) await this.loadInline();
    },

    methods: {
        async loadInline() {
            this.isInterno = this.inlineTipo === 'interno';
            this.isNew = !this.inlineId;
            this.resetForm();
            if (this.isNew) {
                this.dataISO    = this.inlineData || new Date().toISOString();
                this.dataLocale = gLocalData(new Date(this.dataISO));
                await this.loadDatiNew();
            } else {
                await this.loadProgramma(this.inlineId);
            }
        },
        chiudi() {
            if (this.isInline) this.$emit('close');
            else this.$router.back();
        },
        localData(v) { return gLocalData(v); },

        fmtData(d) {
            if (!d) return '';
            return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },

        initiali(nome) {
            if (!nome) return '?';
            const p = nome.trim().split(/\s+/);
            return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : nome[0].toUpperCase();
        },

        resetForm() {
            this.activeSlaveView        = null;
            this.oratoreSelezionato     = null;
            this.oratoreDataSelezionato = null;
            this.congregazioneSelezionata = null;
            this.discorsoId           = null;
            this.cantico              = null;
            this.isProvvisorio        = true;
            this.filtroOratori        = 'disponibili';
            this.soloSchemiNonSvolti  = true;
            this.congregazioneLocked  = false;
            this.schemaFirstMode      = false;
            this.oratoriPerSchemaIds  = [];
            this.schemi               = [];
            this.listaSchemiAll       = [];
            this.listaOratori         = [];
            this.listaCongregazioni   = [];
            this.formSubmitted        = false;
            this.programma            = { oratore: null, congregazione: null, discorso: {}, cantico: 0 };
            this.$nextTick(() => { this.$refs.form && this.$refs.form.resetValidation(); });
        },

        // ── Caricamento iniziale per nuovo ──────────────────────────────
        async loadDatiNew() {
            this.loadingInit = true;
            try {
                if (!this.isInterno) {
                    // Esterno: sempre confermato
                    this.isProvvisorio = false;
                    await this.loadCongregazioni();
                } else {
                    // Interno: carica congregazioni + oratori (arrivo = nostra cong, appartenenza = null → tutte) + schemi
                    await Promise.all([
                        this.loadCongregazioni(),
                        this.loadOratori(this.idCongregazioneAttiva, null),
                        this.loadSchemiAll(),
                    ]);
                }
            } finally {
                this.loadingInit = false;
            }
        },

        // ── Tutti gli schemi con storico (per schema-first) ─────────────
        async loadSchemiAll() {
            this.loadingSchemiAll = true;
            try {
                const lista = await callService('/api/discorso/listaInfo', [
                    this.$store.state.idCircoscrizione,
                    this.idCongregazioneAttiva,
                    true,   // isInterno
                    false,  // ultimoAnno
                ]) || [];
                // "Da stabilire" (diId=99999) è in fondo: spostalo in cima
                const idxDs = lista.findIndex(s => s.discorso?.diId === 99999);
                if (idxDs > 0) lista.unshift(lista.splice(idxDs, 1)[0]);
                this.listaSchemiAll = lista;
            } catch { this.listaSchemiAll = []; }
            finally { this.loadingSchemiAll = false; }
        },

        async loadCongregazioni() {
            try {
                const lista = await callService('/api/congregazione/lista', [
                    this.$store.state.idCircoscrizione
                ]);
                const tutte = lista || [];
                this.congregazioneCorrenteAttiva = tutte.find(c => c.id === this.idCongregazioneAttiva)
                    || this.$store.state.congregazioneCorrente;
                this.listaCongregazioni = tutte
                    .filter(c => this.isInterno || c.id !== this.idCongregazioneAttiva)
                    .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'it'));
            } catch { this.listaCongregazioni = []; }
        },

        // ── Oratori con disponibilità via listaOratoreData ──────────────
        // idCongregazioneAppartenenza: se undefined → nostra cong (esterno), se null → tutte (interno)
        async loadOratori(idCongregazioneArrivo, idCongregazioneAppartenenza) {
            this.loadingOratori = true;
            this.listaOratori   = [];
            this.oratoreSelezionato = null;
            this.schemi  = [];
            this.discorsoId = null;
            const appartId = idCongregazioneAppartenenza !== undefined
                ? idCongregazioneAppartenenza   // null = tutte le congregazioni
                : this.idCongregazioneAttiva; // default esterno = nostra cong
            try {
                const result = await callPost('/api/oratore/listaOratoreData', {
                    partition:                  this.$store.state.partition,
                    username:                   this.$store.state.oratore.codice,
                    token:                      this.$store.state.oratore.token,
                    idCircoscrizione:           this.$store.state.idCircoscrizione,
                    idDiscorso:                 null,
                    data:                       new Date(this.dataISO).toISOString(),
                    idCongregazioneAppartenenza: appartId,
                    idCongregazioneArrivo:      idCongregazioneArrivo,
                    soloDisponibili:            this.filtroOratori === 'disponibili',
                    soloRiserva:                this.filtroOratori === 'riserva',
                });
                this.listaOratori = result || [];
            } catch { this.listaOratori = []; }
            finally { this.loadingOratori = false; }
        },

        // ── Handler slave views ─────────────────────────────────────────
        onCongSelected(cong) {
            this.activeSlaveView = null;
            if (this.isInterno) {
                this.onCongregazioneInternoChange(cong);
                this.congregazioneSelezionata = cong;
            } else {
                this.congregazioneSelezionata = cong;
                this.onCongregazioneChange(cong);
            }
        },
        onOratoreSelected(od) {
            this.activeSlaveView = null;
            this.onOratoreDataChange(od);
        },
        async onSchemaSelected(discorsoId) {
            this.activeSlaveView = null;
            this.discorsoId = discorsoId;
            await this.onSchemaChange(discorsoId);
        },
        async onFiltroOratoriChangeSlave(val) {
            this.filtroOratori = val;
            // Preserva schema-first se attivo (loadOratori azzera discorsoId)
            const savedDiscorsoId    = this.schemaFirstMode ? this.discorsoId : null;
            const savedSchemaFirst   = this.schemaFirstMode;
            const savedOratoriPerIds = this.schemaFirstMode ? [...this.oratoriPerSchemaIds] : [];
            await this.onFiltroOratoriChange();
            if (savedSchemaFirst) {
                this.schemaFirstMode     = true;
                this.discorsoId          = savedDiscorsoId;
                this.oratoriPerSchemaIds = savedOratoriPerIds;
            }
        },

        // ── Filtro autocomplete (cerca su nominativo) ───────────────────
        filtroOratore(item, queryText) {
            const q = queryText.toLowerCase();
            return (item.oratore.nominativo || '').toLowerCase().includes(q);
        },
        // ── Filtro schema (cerca su numero o testo tema) ────────────────
        filtroSchema(item, queryText) {
            const q = queryText.toLowerCase();
            const d = item.discorso || item;
            const num = String(d.diId === 99999 ? '-' : (d.diId || '')).toLowerCase();
            const tema = (d.tema || '').toLowerCase();
            return num.includes(q) || tema.includes(q);
        },

        // ── Selezione oratore dall'autocomplete ─────────────────────────
        async onOratoreDataChange(od) {
            this.oratoreDataSelezionato = od;
            if (!od) {
                this.oratoreSelezionato = null;
                this.schemi = [];
                this.discorsoId = null;
                // Sblocca congregazione se era stata auto-impostata
                if (this.congregazioneLocked) {
                    this.congregazioneSelezionata = null;
                    this.congregazioneLocked = false;
                }
                return;
            }
            this.oratoreSelezionato = od.oratore;

            // INTERNO: auto-set congregazione provenienza se non già scelta dall'utente,
            // oppure aggiorna se era stata auto-impostata da una scelta precedente (locked)
            if (this.isInterno && (!this.congregazioneSelezionata || this.congregazioneLocked)) {
                const congId = od.oratore.congregazione?.id;
                const cong = this.listaCongregazioni.find(c => c.id === congId);
                if (cong) {
                    this.congregazioneSelezionata = cong;
                    this.congregazioneLocked = true;
                }
            }

            // Carica schemi; se schema-first mantieni il discorsoId già scelto
            const preserveId = this.schemaFirstMode ? this.discorsoId : null;
            await this.fetchSchemi(od.oratore.id, preserveId);
        },

        // ── Cambio congregazione ESTERNO ────────────────────────────────
        async onCongregazioneChange(cong) {
            this.oratoreSelezionato     = null;
            this.oratoreDataSelezionato = null;
            this.schemi     = [];
            this.discorsoId = null;
            if (!cong) { this.listaOratori = []; return; }
            await this.loadOratori(cong.id);
        },

        // ── Cambio congregazione INTERNO (filtro, non ricarica) ─────────
        onCongregazioneInternoChange(cong) {
            // Reset oratore e schema quando l'utente cambia congregazione
            this.oratoreDataSelezionato = null;
            this.oratoreSelezionato     = null;
            this.congregazioneLocked    = false;
            if (!this.schemaFirstMode) {
                this.discorsoId = null;
                this.schemi     = [];
            }
            // La lista oratori non viene ricaricata: è già completa, filtriamo via computed
        },

        // ── Azzera tutti i filtri (bottone in cima) ─────────────────────
        azzeraFiltri() {
            this.congregazioneSelezionata = null;
            this.congregazioneLocked      = false;
            this.oratoreDataSelezionato   = null;
            this.oratoreSelezionato       = null;
            this.discorsoId               = null;
            this.schemi                   = [];
            this.schemaFirstMode          = false;
            this.oratoriPerSchemaIds      = [];
            this.soloSchemiNonSvolti      = true;
        },

        // ── Schema cambiato (solo interno per schema-first) ─────────────
        async onSchemaChange(discorsoId) {
            if (!this.isNew) return;   // in modifica aggiorna solo discorsoId, senza schema-first
            if (!this.isInterno) return;
            if (!discorsoId) {
                this.schemaFirstMode     = false;
                this.oratoriPerSchemaIds = [];
                return;
            }
            if (this.oratoreSelezionato) return; // oratore già scelto: solo cambio schema normale
            // Schema-first: carica gli oratori che hanno questo schema
            this.schemaFirstMode = true;
            this.loadingOratori  = true;
            try {
                const lista = await callService('/api/oratore/oratidiscorso', [
                    this.$store.state.idCircoscrizione,
                    discorsoId,
                ]);
                this.oratoriPerSchemaIds = (lista || [])
                    .map(od => od.oratore?.id)
                    .filter(Boolean);
            } catch { this.oratoriPerSchemaIds = []; }
            finally { this.loadingOratori = false; }
        },

        async onFiltroOratoriChange() {
            if (this.isInterno) {
                // Interno: arrivo = nostra cong, appartenenza = null (tutte le congregazioni)
                await this.loadOratori(this.idCongregazioneAttiva, null);
            } else if (this.congregazioneSelezionata) {
                // Esterno: appartenenza = nostra cong (default)
                await this.loadOratori(this.congregazioneSelezionata.id);
            }
        },

        // ── (rimosso selezionaOratore - ora gestito da onOratoreDataChange) ──
        async _unused(od) {
            this.oratoreSelezionato = od.oratore;
            await this.fetchSchemi(od.oratore.id);
        },

        // ── Schema ──────────────────────────────────────────────────────
        async fetchSchemi(idOratore, preserveDiscorsoId = null) {
            this.loadingSchemi = true;
            this.schemi     = [];
            this.discorsoId = null;
            try {
                let lista;
                if (this.isInterno) {
                    // listaInfo con idOratore: inserisce "da stabilire" in testa
                    // e riporta ultimaData/ultimoOratore riferiti alla nostra congregazione
                    lista = await callService(`/api/discorso/listaInfo?idOratore=${encodeURIComponent(idOratore)}`, [
                        this.$store.state.idCircoscrizione,
                        this.idCongregazioneAttiva,
                        true,   // isInterno
                        false,  // ultimoAnno
                    ]);
                } else {
                    // listaInfo con isInterno=false: inserisce "da stabilire" in testa
                    lista = await callService(`/api/discorso/listaInfo?idOratore=${encodeURIComponent(idOratore)}`, [
                        this.$store.state.idCircoscrizione,
                        this.idCongregazioneAttiva,
                        false,  // isInterno
                        false,  // ultimoAnno
                    ]);
                }
                this.schemi = lista || [];
                // In modifica ripristina lo schema precedente
                if (preserveDiscorsoId) {
                    const found = this.schemi.find(s => s.discorso?.id === preserveDiscorsoId);
                    this.discorsoId = found ? preserveDiscorsoId : null;
                }
            } catch { this.schemi = []; }
            finally { this.loadingSchemi = false; }
        },

        // ── Caricamento programma esistente (modifica) ──────────────────
        async loadProgramma(id) {
            this.loadingInit = true;
            try {
                const tipo = this.isInterno ? 'interno' : 'esterno';
                const [programma] = await callService(`/api/programma/${tipo}?idProgramma=${encodeURIComponent(id)}`, [
                    this.$store.state.idCircoscrizione,
                    this.idCongregazioneAttiva,
                ]);
                if (!programma) {
                    this.snackError = true;
                    return;
                }
                this.programma     = programma;
                this.dataLocale    = gLocalData(programma.data);
                this.dataISO       = programma.data;
                this.discorsoId    = programma.discorso?.id;
                this.cantico       = programma.cantico > 0 ? programma.cantico : null;
                this.isProvvisorio = programma.isProvvisorio;

                if (programma.oratore) await this.fetchSchemi(programma.oratore.id, programma.discorso?.id);
            } catch {
                this.errMsg = 'Errore nel caricamento';
                this.snackError = true;
            } finally {
                this.loadingInit = false;
            }
        },

        // ── Salva ───────────────────────────────────────────────────────
        async salva(conferma = false) {
            // Il bottone è disabled se formValida è false, questo è solo una guardia di sicurezza
            if (!this.formValida) return;

            this.loadingSave = true;
            try {
                let programmaPayload;

                if (this.isNew) {
                    programmaPayload = {
                        data: new Date(this.dataISO).toISOString(),
                        oratore: { id: this.oratoreSelezionato.id },
                        // congregazione = destinazione (per interno = nostra, per esterno = la scelta)
                        congregazione: this.isInterno
                            ? { id: this.idCongregazioneAttiva }
                            : { id: this.congregazioneSelezionata.id },
                        // congregazioneEsterna = provenienza (per interno = cong oratore, per esterno = nostra)
                        // Usiamo la congregazione REALE dell'oratore, non il campo UI (che può essere desincronizzato)
                        congregazioneEsterna: this.isInterno
                            ? { id: this.oratoreSelezionato.congregazione.id }
                            : { id: this.idCongregazioneAttiva },
                        discorso:    { id: this.discorsoId },
                        cantico:     parseInt(this.cantico) || 0,
                        isStandard:  true,
                        isProvvisorio: this.isPrenota,  // Prenota → provvisorio, Salva → confermato
                    };
                } else {
                    programmaPayload = {
                        ...this.programma,
                        discorso:    { id: this.discorsoId },
                        cantico:     parseInt(this.cantico) || 0,
                        isProvvisorio: this.isProvvisorio,
                    };
                }

                const endpoint = this.isNew ? '/api/programma/salva' : '/api/programma/aggiornaSchema';
                const result = await callPost(endpoint, {
                    partition:        this.$store.state.partition,
                    username:         this.$store.state.oratore.codice,
                    token:            this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    programma:        programmaPayload,
                    interno:          this.isInterno,
                });

                if (result) {
                    if (conferma) {
                        const confResult = await callPost('/api/programma/conferma', {
                            partition:        this.$store.state.partition,
                            username:         this.$store.state.oratore.codice,
                            token:            this.$store.state.oratore.token,
                            idCircoscrizione: this.$store.state.idCircoscrizione,
                            programma:        {
                                id:                   result.id,
                                congregazione:        result.congregazione        ? { id: result.congregazione.id }        : null,
                                congregazioneEsterna: result.congregazioneEsterna ? { id: result.congregazioneEsterna.id } : null,
                            },
                            interno:          false,
                        });
                        bus.$emit('update-programma', confResult || result);
                    } else {
                        bus.$emit(this.isNew ? 'add-programma' : 'update-programma', result);
                    }
                    if (this.isInline) {
                        this.isNew = false;
                        await this.loadProgramma(result.id);
                    } else {
                        this.chiudi();
                    }
                } else {
                    this.errMsg = 'Salvataggio fallito';
                    this.snackError = true;
                }
            } catch {
                this.errMsg = 'Errore durante il salvataggio';
                this.snackError = true;
            } finally {
                this.loadingSave = false;
            }
        },
    },
};
</script>

<style scoped>
.field-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(0,0,0,0.55);
    margin-bottom: 4px;
    display: flex;
    align-items: center;
}
.theme--dark .field-label { color: rgba(255,255,255,0.5); }

.numero-schema {
    font-size: 0.72rem;
    font-weight: 700;
    padding: 1px 7px;
    border-radius: 6px;
    background-color: rgba(0,0,0,0.08);
    white-space: nowrap;
    flex-shrink: 0;
}
.theme--dark .numero-schema { background-color: rgba(255,255,255,0.12); }

/* Item nel dropdown oratori */
.ora-item {
    width: 100%;
    padding: 6px 0;
}
.ora-item-indisp { opacity: 0.75; }

/* Avviso prenotazione nella lista dropdown */
.avviso-prenot {
    font-size: 0.68rem;
    color: #E65100;
    display: flex;
    align-items: center;
}

/* Due colonne ultima uscita / ultima visita */
.ora-col {
    flex: 1;
    min-width: 0;
}
.ora-divider {
    width: 1px;
    background-color: rgba(0,0,0,0.1);
    margin: 0 4px;
    align-self: stretch;
}
.theme--dark .ora-divider { background-color: rgba(255,255,255,0.12); }

.ora-label {
    font-size: 0.60rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0,0,0,0.38);
    margin-bottom: 2px;
}
.theme--dark .ora-label { color: rgba(255,255,255,0.35); }

.ora-valore {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(0,0,0,0.75);
    white-space: nowrap;
}
.theme--dark .ora-valore { color: rgba(255,255,255,0.75); }

.ora-mai {
    color: rgba(0,0,0,0.35) !important;
    font-weight: 400 !important;
}
.theme--dark .ora-mai { color: rgba(255,255,255,0.3) !important; }

.ora-schema {
    font-size: 0.68rem;
    font-weight: 700;
    color: rgba(0,0,0,0.5);
    background: rgba(0,0,0,0.06);
    border-radius: 4px;
    padding: 0 5px;
    display: inline-block;
    margin-top: 2px;
}
.theme--dark .ora-schema {
    color: rgba(255,255,255,0.55);
    background: rgba(255,255,255,0.08);
}

.ora-cong {
    font-size: 0.65rem;
    color: rgba(0,0,0,0.45);
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.theme--dark .ora-cong { color: rgba(255,255,255,0.35); }

.ora-motivazione {
    font-size: 0.7rem;
    color: #c62828;
    display: flex;
    align-items: center;
}
.theme--dark .ora-motivazione { color: #ef9a9a; }

.selezionato-card {
    cursor: pointer;
    border: 1px solid rgba(25, 118, 210, 0.35) !important;
    transition: box-shadow 0.15s;
}
.selezionato-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.13) !important; }
.selezionato-locked {
    cursor: default;
    border-color: rgba(0,0,0,0.15) !important;
}
.selezionato-locked:hover { box-shadow: none !important; }
</style>
